import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated, selectUserRole } from "store/slices/authSlice";
import { hasAnyPermission } from "config/roles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

/**
 * ProtectedRoute — Wraps routes with auth + RBAC checks
 * 
 * Props:
 *   - requiredPermissions: string[] — permissions needed to access this route
 *   - children: ReactNode — the page component to render
 * 
 * Behavior:
 *   1. If not authenticated → redirect to /authentication/sign-in
 *   2. If authenticated but no required permission → show Unauthorized page
 *   3. If authenticated and has permission → render children
 */
function ProtectedRoute({ requiredPermissions = [], children }) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);
  const location = useLocation();

  // Not logged in → redirect to sign-in
  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }

  // Check permissions (empty array = no restriction, just needs auth)
  if (requiredPermissions.length > 0 && !hasAnyPermission(userRole, requiredPermissions)) {
    return <UnauthorizedPage />;
  }

  // All checks passed
  return children;
}

/**
 * Unauthorized page shown when user doesn't have the required role
 */
function UnauthorizedPage() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={10}
        px={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Icon sx={{ fontSize: "64px !important", color: "#E74C3C", mb: 2 }}>lock</Icon>
        <MDTypography variant="h4" fontWeight="bold" color="dark" mb={1}>
          Access Denied
        </MDTypography>
        <MDTypography variant="body2" color="secondary" mb={3} sx={{ maxWidth: 400 }}>
          You don't have permission to access this page. Contact your administrator if you believe this is an error.
        </MDTypography>
        <MDButton variant="gradient" color="info" href="/home">
          <Icon>home</Icon>&nbsp;Back to Home
        </MDButton>
      </MDBox>
    </DashboardLayout>
  );
}

export default ProtectedRoute;
